import { useQuery } from '@tanstack/react-query';

export interface CurrentUserTenant {
  id: string;
  name: string;
  slug: string;
  templateId: string;
  primaryColor?: string;
  logoUrl?: string;
}

export interface CurrentUser {
  id: string;
  tenantId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: 'user' | 'admin' | 'super_admin';
  isActive: boolean;
  acceptedTermsAt: string | null;
  termsVersion: string | null;
  tenant: CurrentUserTenant | null;
  /** Set when a super_admin is impersonating this user */
  impersonatedBy?: string | null;
}

export function useCurrentUser() {
  return useQuery<CurrentUser>({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const response = await fetch('/api/me', {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to fetch current user');
      }
      return response.json();
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: false, // Don't retry if unauthorized
  });
}

export function useIsAdmin() {
  const { data: user, isLoading } = useCurrentUser();
  return {
    isAdmin: user?.role === 'admin' || user?.role === 'super_admin',
    isSuperAdmin: user?.role === 'super_admin',
    isLoading,
  };
}
